<script setup lang="ts">
import type { Car, CreateCarInput, FuelType, UpdateCarInput } from '../internal/types.ts'
import { localDateParts, parseLocalDate } from '@taler/domain'
import { ActionButton, Select, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  mode: 'create' | 'edit'
  initial?: Car
  isSubmitting: boolean
  errorMessage: string | undefined
}>()
const emit = defineEmits<{ submit: [CreateCarInput | UpdateCarInput]; cancel: [] }>()

const { t } = useI18n()

const name = ref('')
const make = ref('')
const model = ref('')
const year = ref('')
const color = ref('')
const licensePlate = ref('')
const vin = ref('')
const fuelType = ref<FuelType | undefined>(undefined)
const initialMileageKm = ref('')
const purchaseDate = ref('')
const notes = ref('')
const isPrimary = ref(false)
const validationError = ref<string | undefined>(undefined)

function loadInitial(): void {
  const car = props.initial
  if (car === undefined) return
  name.value = car.name
  make.value = car.make ?? ''
  model.value = car.model ?? ''
  year.value = car.year === undefined ? '' : String(car.year)
  color.value = car.color ?? ''
  licensePlate.value = car.licensePlate ?? ''
  vin.value = car.vin ?? ''
  fuelType.value = car.fuelType
  initialMileageKm.value = String(car.initialMileageKm)
  purchaseDate.value = car.purchaseDate === undefined ? '' : formatDateInput(car.purchaseDate)
  notes.value = car.notes ?? ''
}
watch(() => props.initial, loadInitial, { immediate: true })

function formatDateInput(value: ReturnType<typeof parseLocalDate>): string {
  const { year, month, day } = localDateParts(value)
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

const fuelTypeOptions = computed(() => [
  { value: 'DIESEL' as const, label: t('garage.fuelType.DIESEL') },
  { value: 'PETROL' as const, label: t('garage.fuelType.PETROL') },
  { value: 'ELECTRIC' as const, label: t('garage.fuelType.ELECTRIC') },
  { value: 'HYBRID' as const, label: t('garage.fuelType.HYBRID') },
  { value: 'GAS' as const, label: t('garage.fuelType.GAS') },
])

function strOrUndef(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

function submit(): void {
  validationError.value = undefined
  if (name.value.trim() === '') return
  if (props.mode === 'create' && fuelType.value === undefined) {
    validationError.value = t('garage.form.invalidAmount')
    return
  }
  const yearValue = year.value === '' ? undefined : parseInt(year.value, 10)
  const mileageValue = initialMileageKm.value === '' ? undefined : parseInt(initialMileageKm.value, 10)
  const purchaseDateValue = purchaseDate.value === '' ? undefined : parseLocalDate(purchaseDate.value)

  if (props.mode === 'create') {
    const input: CreateCarInput = {
      name: name.value.trim(),
      fuelType: fuelType.value!,
      initialMileageKm: mileageValue ?? 0,
      isPrimary: isPrimary.value,
      ...(strOrUndef(make.value) === undefined ? {} : { make: strOrUndef(make.value)! }),
      ...(strOrUndef(model.value) === undefined ? {} : { model: strOrUndef(model.value)! }),
      ...(yearValue === undefined ? {} : { year: yearValue }),
      ...(strOrUndef(licensePlate.value) === undefined
        ? {}
        : { licensePlate: strOrUndef(licensePlate.value)! }),
      ...(strOrUndef(vin.value) === undefined ? {} : { vin: strOrUndef(vin.value)! }),
      ...(strOrUndef(color.value) === undefined ? {} : { color: strOrUndef(color.value)! }),
      ...(purchaseDateValue === undefined ? {} : { purchaseDate: purchaseDateValue }),
      ...(strOrUndef(notes.value) === undefined ? {} : { notes: strOrUndef(notes.value)! }),
    }
    emit('submit', input)
    return
  }

  const input: UpdateCarInput = {
    name: name.value.trim(),
    ...(fuelType.value === undefined ? {} : { fuelType: fuelType.value }),
    ...(mileageValue === undefined ? {} : { initialMileageKm: mileageValue }),
    make: strOrUndef(make.value) ?? '',
    model: strOrUndef(model.value) ?? '',
    ...(yearValue === undefined ? {} : { year: yearValue }),
    licensePlate: strOrUndef(licensePlate.value) ?? '',
    vin: strOrUndef(vin.value) ?? '',
    color: strOrUndef(color.value) ?? '',
    ...(purchaseDateValue === undefined ? {} : { purchaseDate: purchaseDateValue }),
    notes: strOrUndef(notes.value) ?? '',
  }
  emit('submit', input)
}

const combinedError = computed(() => validationError.value ?? props.errorMessage)
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="submit">
    <div class="grid grid-cols-2 gap-[var(--space-cell)]">
      <label class="col-span-2 flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.nameLabel')
        }}</span>
        <input
          v-model="name"
          type="text"
          maxlength="100"
          required
          :placeholder="t('garage.form.namePlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.make')
        }}</span>
        <input
          v-model="make"
          type="text"
          :placeholder="t('garage.form.makePlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.model')
        }}</span>
        <input
          v-model="model"
          type="text"
          :placeholder="t('garage.form.modelPlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.year')
        }}</span>
        <input
          v-model="year"
          type="number"
          :placeholder="t('garage.form.yearPlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.color')
        }}</span>
        <input
          v-model="color"
          type="text"
          :placeholder="t('garage.form.colorPlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.licensePlate')
        }}</span>
        <input
          v-model="licensePlate"
          type="text"
          :placeholder="t('garage.form.licensePlatePlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.vin')
        }}</span>
        <input
          v-model="vin"
          type="text"
          maxlength="17"
          :placeholder="t('garage.form.vinPlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.fuelTypeLabel')
        }}</span>
        <Select
          v-model="fuelType"
          :options="fuelTypeOptions"
          :placeholder="t('garage.form.selectFuelType')"
          aria-label="Fuel type"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.initialMileage')
        }}</span>
        <input
          v-model="initialMileageKm"
          type="number"
          min="0"
          :placeholder="t('garage.form.initialMileagePlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.purchaseDate')
        }}</span>
        <input
          v-model="purchaseDate"
          type="date"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <label v-if="mode === 'create'" class="flex items-center gap-2 text-[color:var(--color-text)]">
        <input v-model="isPrimary" type="checkbox" />
        {{ t('garage.form.makePrimary') }}
      </label>

      <label class="col-span-2 flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.form.notes')
        }}</span>
        <textarea
          v-model="notes"
          rows="2"
          :placeholder="t('garage.form.notesPlaceholder')"
          class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] py-2 text-[color:var(--color-text)]"
        />
      </label>
    </div>

    <StatusTag v-if="combinedError" tone="danger">{{ combinedError }}</StatusTag>

    <div class="flex gap-2">
      <ActionButton type="submit" :disabled="isSubmitting">
        {{
          isSubmitting
            ? t('garage.form.saving')
            : mode === 'create'
              ? t('garage.form.create')
              : t('garage.form.save')
        }}
      </ActionButton>
      <button
        type="button"
        class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
        @click="$emit('cancel')"
      >
        {{ t('garage.form.cancel') }}
      </button>
    </div>
  </form>
</template>
